import React, { useState, useEffect } from 'react';
import { Button, Spinner } from 'reactstrap';
import { fetchApplication, getApplicationWithStatusValidation } from '../../helpers/Espo/application_helper';
import ApplicationPDFGenerator from './ApplicationPDFGenerator';
import SignedApplicationUploadModal from '../Common/SignedApplicationUploadModal';
import { 
    getStatusPermissions, 
    canPerformAction, 
    APPLICATION_STATUS 
} from '../../helpers/statusManager';

const ApplicationPDFHandler = ({ applicationId, applicantName, status, applicationNumber }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [applicationData, setApplicationData] = useState(null);
    const [error, setError] = useState(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    
    // Get status-based permissions
    const statusPermissions = getStatusPermissions(status);

    console.log('ApplicationPDFHandler received applicationId:', applicationId, 'applicantName:', applicantName, 'applicationNumber:', applicationNumber, 'status:', status, 'permissions:', statusPermissions);

    // Load the application schema
    const aidApplicationSchema = require('../../Pages/Forms/Schema/AidApplication.json');
    const sortedSchema = aidApplicationSchema.sort((a, b) => a.id - b.id);

    // Load application data on component mount
    useEffect(() => {
        const loadApplicationData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const fetchedData = await getApplicationWithStatusValidation(applicationId);
                console.log('ApplicationPDFHandler - Application data loaded:', {
                    applicationId,
                    status,
                    applicationSignedFormId: fetchedData?.applicationSignedFormId,
                    fetchedData
                });
                setApplicationData(fetchedData);
            } catch (err) {
                console.error('Error fetching application data for PDF:', err);
                setError('Failed to fetch application data');
            } finally {
                setIsLoading(false);
            }
        };

        if (applicationId) {
            loadApplicationData();
        }
    }, [applicationId]);

    const toggleUploadModal = () => {
        setIsUploadModalOpen(!isUploadModalOpen);
    };

    const handleUploadSuccess = (result) => {
        // Optionally refresh application data or reload the page to show updated status
        console.log('Upload successful:', result);
        
        // Reload the page to show the updated status
        window.location.reload();
    };

    // Show different buttons based on status
    if (status === APPLICATION_STATUS.DRAFT || status === APPLICATION_STATUS.MISSING_INFORMATION) {
        return null; // No PDF actions for draft or missing information
    }

    // Debug logging for button visibility
    console.log('ApplicationPDFHandler render conditions:', {
        applicationId,
        status,
        applicationData: !!applicationData,
        applicationSignedFormId: applicationData?.applicationSignedFormId,
        statusPermissions,
        canDownloadPDF: statusPermissions.canDownloadPDF,
        canUploadSignedForm: statusPermissions.canUploadSignedForm,
        canDownloadSignedForm: statusPermissions.canDownloadSignedForm
    });

    return (
        <>
            <div className="d-flex gap-1">
                {/* Download Form Button - Only show when user can download PDF and upload signed form */}
                {statusPermissions.canDownloadPDF && statusPermissions.canUploadSignedForm && (
                    <>
                        {/* PDF Download Button */}
                        {isLoading ? (
                            <Button
                                size="sm"
                                color="primary"
                                outline
                                disabled
                                title="Loading application data..."
                            >
                                <Spinner size="sm" />
                            </Button>
                        ) : applicationData && !error ? (
                            <ApplicationPDFGenerator
                                schema={sortedSchema}
                                formData={applicationData}
                                applicantName={applicantName}
                                fileName={`Application_${applicantName || 'Unknown'}_${applicationNumber || applicationId}.pdf`}
                                buttonSize="sm"
                                buttonColor="info"
                                buttonStyle={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                }}
                                buttonIcon="fas fa-download"
                                buttonText=""
                                buttonTitle="Download Form to Sign"
                            />
                        ) : null}
                    </>
                )}

                {/* Upload Signed Form Button - Show when allowed by status permissions */}
                {statusPermissions.canUploadSignedForm && !isLoading && applicationData && !error && (
                    <Button
                        size="sm"
                        color="success"
                        outline
                        onClick={toggleUploadModal}
                        title="Upload Signed PDF"
                        className="ms-1"
                    >
                        <i className="fas fa-upload"></i>
                        <span className="d-none d-md-inline ms-1">Upload Signed PDF</span>
                    </Button>
                )}

                {/* Download Signed Form Button - Only show when signed form exists AND user can download it */}
                {applicationData && applicationData.applicationSignedFormId && statusPermissions.canDownloadSignedForm && (
                    <Button
                        size="sm"
                        color="info"
                        outline
                        onClick={async () => {
                            const fileUrl = `${process.env.REACT_APP_ESPO_API_URL}/Attachment/file/${applicationData.applicationSignedFormId}`;
                            console.log('Attempting to download signed form:', {
                                status,
                                applicationSignedFormId: applicationData.applicationSignedFormId,
                                apiUrl: process.env.REACT_APP_ESPO_API_URL,
                                fullUrl: fileUrl
                            });
                            
                            try {
                                // Fetch the file with proper headers
                                const response = await fetch(fileUrl, {
                                    headers: {
                                        'x-api-key': process.env.REACT_APP_ESPO_API_KEY || '3d464d1f4404b17969165f0eaeb45926'
                                    }
                                });
                                
                                if (!response.ok) {
                                    throw new Error(`HTTP error! status: ${response.status}`);
                                }
                                
                                // Get the blob data
                                const blob = await response.blob();
                                
                                // Create a download link
                                const downloadUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = downloadUrl;
                                link.download = `Signed_Application_${applicationNumber || applicationId}.pdf`;
                                link.style.display = 'none';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                
                                // Clean up the object URL
                                window.URL.revokeObjectURL(downloadUrl);
                            } catch (error) {
                                console.error('Error downloading signed form:', error);
                                // Fallback to simple link approach
                                window.location.href = fileUrl;
                            }
                        }}
                        title="Download Signed Form"
                    >
                        <i className="fas fa-download"></i>
                        <span className="d-none d-md-inline ms-1">Download Signed Form</span>
                    </Button>
                )}

                {/* For old "Submitted" status without signed form - show download only if PDF download is allowed */}
                {status === "Submitted" && (!applicationData || !applicationData.applicationSignedFormId) && statusPermissions.canDownloadPDF && (
                    <>
                        {isLoading ? (
                            <Button
                                size="sm"
                                color="primary"
                                outline
                                disabled
                                title="Loading application data..."
                            >
                                <Spinner size="sm" />
                            </Button>
                        ) : applicationData && !error ? (
                            <ApplicationPDFGenerator
                                schema={sortedSchema}
                                formData={applicationData}
                                applicantName={applicantName}
                                fileName={`Application_${applicantName || 'Unknown'}_${applicationNumber || applicationId}.pdf`}
                                buttonSize="sm"
                                buttonColor="info"
                                buttonStyle={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                }}
                                buttonIcon="fas fa-download"
                                buttonText=""
                                buttonTitle="Download PDF"
                            />
                        ) : null}
                    </>
                )}
            </div>

            {/* Upload Modal - only show for "Pending Signed Form" status */}
            {status === "Pending Signed Form" && (
                <SignedApplicationUploadModal
                    isOpen={isUploadModalOpen}
                    toggle={toggleUploadModal}
                    applicationId={applicationId}
                    applicantName={applicantName}
                    applicationNumber={applicationNumber}
                    onUploadSuccess={handleUploadSuccess}
                />
            )}

            {/* Error Display */}
            {error && (
                <div className="text-danger small mt-1">
                    {error}
                </div>
            )}
        </>
    );
};

export default ApplicationPDFHandler;
